// Winner reveal modal — a cinematic, golden celebration. Accessible dialog:
// focus-trapped, Esc to close, backdrop click to dismiss, focus restored to the
// trigger on close. Visual layers: blurred backdrop, rotating light rays, the
// celebration prize image as the hero, premium spring entrance.

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RAFFLE_NS } from './translations';
import { CloseIcon, RefreshIcon, CopyIcon, CheckIcon } from './icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const WinnerModal = ({
  open,
  winner,
  prizeTitle,
  winnerImage,
  prizeImage,
  onClose,
  onAgain,
  reducedMotion = false,
}) => {
  const { t } = useTranslation(RAFFLE_NS);
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Celebration image with a graceful fallback chain: dedicated winner image →
  // the won prize's image → nothing (each level is skipped if its file 404s).
  const imageSources = [winnerImage, prizeImage].filter(Boolean);
  const [imgIndex, setImgIndex] = useState(0);
  const celebrationSrc = imageSources[imgIndex];

  // Restart the chain whenever the set of candidate images changes.
  useEffect(() => {
    setImgIndex(0);
  }, [winnerImage, prizeImage]);

  // Copies a link to the winner's admin profile — never navigates there
  // itself, the admin opens it in a new tab on their own.
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [winner]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const handleCopyProfileLink = useCallback(async () => {
    if (!winner?.userId) return;
    const url = `${window.location.origin}/users/${winner.userId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (e) {
      /* ignore clipboard errors */
    }
  }, [winner]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = dialogRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || !nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const node = dialogRef.current;
    const firstButton = node?.querySelector(FOCUSABLE);
    firstButton?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && winner && (
        <motion.div
          className="rf-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
          onKeyDown={handleKeyDown}
        >
          <motion.div
            ref={dialogRef}
            className="rf-modal__card rf-grain"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rf-winner-title"
            initial={
              reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.82, y: 36, rotate: -1.5 }
            }
            animate={
              reducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0, rotate: 0 }
            }
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 24 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
          >
            <span className="rf-modal__glow" aria-hidden="true" />
            {!reducedMotion && <span className="rf-modal__rays" aria-hidden="true" />}

            <button
              type="button"
              className="rf-modal__close"
              aria-label={t('winner.close')}
              onClick={onClose}
            >
              <CloseIcon size={20} />
            </button>

            {celebrationSrc && (
              <motion.div
                className={`rf-modal__prize${
                  celebrationSrc === winnerImage ? ' rf-modal__prize--celebration' : ''
                }`}
                aria-hidden="true"
                initial={reducedMotion ? false : { scale: 0.4, opacity: 0 }}
                animate={reducedMotion ? undefined : { scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.12 }}
              >
                <img
                  className="rf-modal__prize-img"
                  src={celebrationSrc}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={() => setImgIndex((i) => i + 1)}
                />
              </motion.div>
            )}

            <p className="rf-modal__congrats">{t('winner.congrats')}</p>
            <h2 id="rf-winner-title" className="rf-modal__name rf-grad-text">
              {winner.fullName}
            </h2>
            <p className="rf-modal__wins">
              {t('winner.wins')} <strong>{prizeTitle}</strong>
            </p>

            <div className="rf-modal__actions">
              <button type="button" className="rf-btn rf-btn--primary" onClick={onAgain}>
                <RefreshIcon size={18} /> {t('winner.again')}
              </button>
              {winner.userId && (
                <button type="button" className="rf-btn rf-btn--ghost" onClick={handleCopyProfileLink}>
                  {copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
                  {copied ? t('winner.copied') : t('winner.copyLink')}
                </button>
              )}
              <button type="button" className="rf-btn rf-btn--ghost" onClick={onClose}>
                {t('winner.close')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WinnerModal;
